// Text provider backed by the Bedrock Converse brain on the assistant server.
// Falls back to the local knowledge base if the server is unreachable, so text
// chat always works even without the backend running.

import type { AssistantProvider, AssistantReply, AssistantAction } from './types'
import { LocalProvider } from './localProvider'

const HTTP_URL = import.meta.env.VITE_ASSISTANT_HTTP_URL ?? 'http://localhost:8080'

class BedrockProvider implements AssistantProvider {
  readonly name = 'bedrock' as const

  async send(text: string, sessionId: string, lang?: string): Promise<AssistantReply> {
    // Hard timeout so a hung request can never stall the conversation turn.
    const controller = new AbortController()
    const timer = window.setTimeout(() => controller.abort(), 45000)
    try {
      const res = await fetch(`${HTTP_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, text, lang }),
        signal: controller.signal,
      })
      if (!res.ok) throw new Error(`chat failed: ${res.status}`)
      const data = (await res.json()) as { text: string; action?: AssistantAction | null }
      return { text: data.text, action: data.action ?? undefined, source: 'bedrock' }
    } finally {
      window.clearTimeout(timer)
    }
  }
}

/**
 * Tries the Bedrock brain first; on any failure (server down, network) falls
 * back to the dependency-free local provider.
 */
export class ResilientTextProvider implements AssistantProvider {
  readonly name = 'bedrock' as const
  private bedrock = new BedrockProvider()
  private local = new LocalProvider()

  async send(text: string, sessionId: string, lang?: string): Promise<AssistantReply> {
    try {
      return await this.bedrock.send(text, sessionId, lang)
    } catch {
      return this.local.send(text, sessionId, lang)
    }
  }
}
